import React, { useEffect, useState, useContext } from "react";
import Error from "containers/Error";
import { useLocation } from "react-router-dom";
import FeaturedProducts from "components/FeaturedProducts";
import { StoreContext } from "components/StoreContext";
import Loading from "components/Loading";
import HeadTags from "components/HeadTags";
import Header from "./components/headerBlock";
import CourseTabs from "./components/courseTabs";
import ProductTabs from "./components/productTabs";
import PartnerDetailsCard from "./components/partnerDetailsCard";

// eslint-disable-next-line consistent-return
const Product = () => {
  const location = useLocation();
  const { store, StoreAPI } = useContext(StoreContext);
  const [loading, updateLoading] = useState(true);
  const [pageError, setError] = useState(undefined);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 680);
  const [product, setProduct] = useState({
    id: null,
    productType: "",
    productName: "",
    sku: "",
    shortDescription: "",
    longDescription: "",
    isActive: false,
    partnerName: "",
    partnerCode: "",
    imageLargePath: "",
    imageMediumPath: "",
    imageThumbNailPath: "",
    image: "",
    imageSmallThumbnailPath: "",
    salePrice: null,
    retailPrice: null,
    currencyCode: "",
    custom1: "",
    custom2: "",
    custom3: "",
    custom4: "",
    custom5: "",
    isMobileAccessible: false,
    isPurchasable: true,
    showPrice: true,
    licenseDuration: null,
    ceCredits: null,
    courseBundleCount: null,
    partnerImage: "",
    partnerDescription: "",
    partnerShortName: "",
    isFeatured: false,
    relatedProducts: "",
    productTags: "",
    productCategoryId: null
  });

  useEffect(() => {
    const getSku = () => {
      const segment = window.location.pathname.split("/")[2];
      const hyphen = segment.lastIndexOf("-");
      return hyphen > -1 ? segment.slice(hyphen + 1) : segment;
    };
    const sku = getSku();

    if (store.loaded) {
      const getProductBySku = () => {
        // this loading helps the page refresh seem less jaring to the user
        updateLoading(true);

        StoreAPI.get(`products/${sku}`)
          .then(response => {
            const { data } = response;
            const obj = {
              id: data.id,
              productType: data.productType,
              productName: data.productName,
              sku: data.sku,
              shortDescription: data.shortDescription,
              longDescription: data.longDescription,
              isActive: data.isActive,
              partnerName: data.partnerName,
              partnerCode: data.partnerCode,
              imageLargePath: data.imageLargePath,
              imageMediumPath: data.imageMediumPath,
              imageThumbNailPath: data.imageThumbNailPath,
              image: data.image,
              imageSmallThumbnailPath: data.imageSmallThumbnailPath,
              salePrice: data.salePrice,
              retailPrice: data.retailPrice,
              currencyCode: data.currencyCode,
              custom1: data.custom1,
              custom2: data.custom2,
              custom3: data.custom3,
              custom4: data.custom4,
              custom5: data.custom5,
              isMobileAccessible: data.isMobileAccessible,
              isPurchasable: data.isPurchasable,
              showPrice: data.showPrice,
              licenseDuration: data.licenseDuration,
              ceCredits: data.ceCredits,
              courseBundleCount: data.courseBundleCount,
              partnerImage: data.partnerImage,
              partnerDescription: data.partnerDescription,
              partnerShortName: data.partnerShortName,
              isFeatured: data.isFeatured,
              relatedProducts: data.relatedProducts,
              productTags: data.productTags,
              productCategoryId: data.productCategoryId,
              seoDescription: data.seoDescription,
              seoKeywords: data.seoKeywords,
              seoTitle: data.seoTitle,
              seoRobots: data.robotTagValue
            };
            setProduct(obj);
            updateLoading(false);
            setError(undefined);
          })
          .catch(error => {
            setError(error);
            updateLoading(false);
          });
      };

      getProductBySku();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.loaded, location]);

  useEffect(() => {
    window.addEventListener(
      "resize",
      () => {
        const ismobile = window.innerWidth < 680;
        if (ismobile !== isMobile) setIsMobile(ismobile);
      },
      false
    );
  }, [isMobile]);

  if (pageError !== undefined) {
    return <Error error={pageError} />;
  }

  if (!pageError && loading) {
    return (
      <div className="site__loading">
        <Loading />
      </div>
    );
  }

  if (!pageError && !loading) {
    return (
      <div className="productPage">
        {product && (
          <>
            <HeadTags
              title={product.seoTitle || product.productName}
              canonical={`/products/${product.sku}`}
              description={product.seoDescription}
              keywords={product.seoKeywords}
              robots={product.seoRobots}
            />
            <Header
              parentCategory={product.id}
              credits={product.ceCredits}
              bundleCount={product.courseBundleCount}
              provider={product.partnerName}
              name={product.productName}
              partner={product.partnerCode}
              sku={product.sku}
              id={product.id}
              price={product.retailPrice}
              showPrice={product.showPrice}
              discountPrice={product.salePrice}
              accessLimit={product.licenseDuration}
              purchasable={product.isPurchasable}
              imageLargePath={product.imageLargePath}
            />
            {product.courseBundleCount === 1 ? (
              <div className="productPage__body">
                <div className="productPage__body--leftCol">
                  {product.courseBundleCount === 1 && (
                    <div className="singleCourseDescription">
                      <h2 className="singleCourseDescription__title">
                        About this course
                      </h2>
                      <p
                        className="singleCourseDescription__text"
                        dangerouslySetInnerHTML={{
                          __html: product.longDescription
                        }}
                      />
                      <CourseTabs courseId={product.id} />
                    </div>
                  )}
                </div>
                <div className="productPage__body--rightCol col-sm-3">
                  <PartnerDetailsCard partner={product.partnerCode} />
                </div>
              </div>
            ) : (
              <div className="productPage__body--multipleProducts">
                {product.courseBundleCount === 1 && (
                  <div className="singleCourseDescription">
                    <h2 className="singleCourseDescription__title">
                      Course Description
                    </h2>
                    <p
                      className="singleCourseDescription__text"
                      dangerouslySetInnerHTML={{
                        __html: product.longDescription
                      }}
                    />
                  </div>
                )}
                <ProductTabs
                  bundleCount={product.courseBundleCount}
                  multiContentDescriptionLong={product.longDescription}
                  multiContentDescriptionShort={product.shortDescription}
                  courseId={product.id}
                  partner={product.partnerCode}
                  parentCheck={product.partnerName}
                />
              </div>
            )}
            <FeaturedProducts
              title="Other Courses to Consider"
              endpoint={`${product.id}/related`}
            />
          </>
        )}
      </div>
    );
  }
};

export default Product;
